import {useState, type MouseEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Propuestas} from '@/components/perfil-propuestas'
import {Tag} from '@/components/ui/tag'
import {Hint} from '@/components/ui/hint'
import {columnaDeAcciones, type AccionDeFila} from '@/components/ui/acciones-de-fila'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DataTable, type Column} from '@/components/data-table'
import {OrigenLinea} from '@/components/origen-linea'
import {CabeceraDeBloque} from '@/components/perfil-bloque-cabecera'
import type {AccionesDeLinea} from '@/components/perfil-bloque-tabla'
import type {PerfilBloqueRead, PerfilLineaRead} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {useIsMobile} from '@/hooks/common/use-media-query'
import {medidasDe, type AperturaRiesgo, type Riesgo} from '@/lib/perfil-bloques'
import {cn} from '@/lib/utils'

// El dato de la fila en el nombre de sus íconos (T11), sin volverlo un párrafo leído en voz alta.
const corto = (texto: string) => (texto.length > 60 ? `${texto.slice(0, 59).trimEnd()}…` : texto)

/** Una fila es un riesgo y se edita entero en un solo diálogo (KAI-334). Las medidas sueltas se
 * quitan todas juntas: el servidor rechaza un borrador con una sola medida suelta. */
export function PerfilBloquePeligros({
  peligros,
  medidas,
  editable,
  pending,
  desplegado,
  businessId,
  onAdoptar,
  onAdoptarTodas,
  onConfirmarLinea,
  onQuitarSueltas,
  onAbrirRiesgo,
  onQuitarRiesgo,
}: {
  peligros: PerfilBloqueRead
  medidas: PerfilBloqueRead | undefined
  onAbrirRiesgo: (apertura: AperturaRiesgo) => void
  onQuitarRiesgo: (riesgo: Riesgo) => void
  onQuitarSueltas: (medidas: PerfilLineaRead[]) => void
} & Pick<
  AccionesDeLinea,
  'editable' | 'pending' | 'desplegado' | 'businessId' | 'onAdoptar' | 'onAdoptarTodas' | 'onConfirmarLinea'
>) {
  const {t} = useTranslation()
  const enTelefono = useIsMobile()
  const [abiertas, setAbiertas] = useState<ReadonlySet<string>>(new Set())
  const [quitandoSueltas, setQuitandoSueltas] = useState(false)
  const medidasGuardadas = medidas?.lineas ?? []
  const medidasPropuestas = medidas?.propuestas ?? []
  const riesgos: Riesgo[] = peligros.lineas.map((peligro) => ({peligro, medidas: medidasDe(peligro, medidasGuardadas)}))
  const grupos = new Set(peligros.lineas.map((peligro) => peligro.grupo_clave))
  // Mismo criterio que `medidasDe`: una medida sin padre no es de ningún riesgo.
  const adoptable = (medida: PerfilLineaRead) => medida.padre_clave !== null && grupos.has(medida.padre_clave)
  const sueltas = medidasGuardadas.filter((medida) => !adoptable(medida))
  const medidasAdoptables = medidasPropuestas.filter(adoptable)

  const abierta = (riesgo: Riesgo) => desplegado || abiertas.has(riesgo.peligro.clave)
  const alternar = (riesgo: Riesgo) => (event: MouseEvent) => {
    // El botón vive en una fila que abre el riesgo: el clic es del botón, no de la fila.
    event.stopPropagation()
    const siguientes = new Set(abiertas)
    if (siguientes.has(riesgo.peligro.clave)) siguientes.delete(riesgo.peligro.clave)
    else siguientes.add(riesgo.peligro.clave)
    setAbiertas(siguientes)
  }

  const clase = (peligro: PerfilLineaRead) =>
    peligro.clasificacion_gtc ? (
      t(`ipevr.clasificacion.${peligro.clasificacion_gtc}`)
    ) : (
      <Tag tone="amber">{t('perfilCargo.tabla.sinClase')}</Tag>
    )

  const celdaMedidas = (riesgo: Riesgo) => {
    const {medidas: lista} = riesgo
    if (lista.length === 0) return null
    if (!abierta(riesgo)) {
      return (
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate">{lista[0].texto}</span>
          {lista.length > 1 && (
            <Button
              type="button"
              size="xs"
              variant="outline"
              aria-expanded={false}
              data-testid={`abrir-medidas-${riesgo.peligro.clave}`}
              onClick={alternar(riesgo)}>
              {t('perfilCargo.riesgo.masMedidas', {count: lista.length - 1})}
            </Button>
          )}
        </div>
      )
    }
    return (
      <div className="space-y-1">
        <ul className="list-disc space-y-1 pl-4">
          {lista.map((medida) => (
            <li key={medida.clave} data-testid={`linea-${medida.clave}`}>
              <span className="whitespace-pre-wrap">{medida.texto}</span>
              <OrigenLinea linea={medida} businessId={businessId} desplegado={desplegado} />
            </li>
          ))}
        </ul>
        {!desplegado && lista.length > 1 && (
          <Button
            type="button"
            size="xs"
            variant="outline"
            aria-expanded
            data-testid={`cerrar-medidas-${riesgo.peligro.clave}`}
            onClick={alternar(riesgo)}>
            {t('perfilCargo.riesgo.verMenos')}
          </Button>
        )}
      </div>
    )
  }

  const acciones = (riesgo: Riesgo): AccionDeFila[] => [
    ...(riesgo.peligro.confirmado
      ? []
      : [
          {
            clave: `confirmar-${riesgo.peligro.clave}`,
            concepto: 'confirmar' as const,
            etiqueta: t('perfilCargo.detalle.confirmarLinea'),
            onActivar: () => onConfirmarLinea(riesgo.peligro),
            deshabilitada: pending,
          },
        ]),
    {
      clave: `editar-${riesgo.peligro.clave}`,
      concepto: 'editar',
      etiqueta: t('perfilCargo.riesgo.editar'),
      onActivar: () => onAbrirRiesgo({modo: 'editar', riesgo}),
      deshabilitada: pending,
    },
    {
      clave: `quitar-${riesgo.peligro.clave}`,
      concepto: 'borrar',
      etiqueta: t('perfilCargo.detalle.quitar'),
      onActivar: () => onQuitarRiesgo(riesgo),
      deshabilitada: pending,
    },
  ]

  const descripcion = (riesgo: Riesgo) => (
    <div className="space-y-1">
      <span className={cn('whitespace-pre-wrap', !abierta(riesgo) && 'line-clamp-2')}>{riesgo.peligro.texto}</span>
      <OrigenLinea linea={riesgo.peligro} businessId={businessId} desplegado={desplegado} />
    </div>
  )
  const estado = (riesgo: Riesgo) => (
    <div className="flex flex-wrap gap-1">
      {riesgo.peligro.confirmado ? (
        <Tag tone="green">{t('perfilCargo.estado.confirmado')}</Tag>
      ) : (
        <Tag tone="amber">{t('perfilCargo.estado.borrador')}</Tag>
      )}
      {/* Un riesgo sin medida no confirma (KAI-146): «No existe» también es una medida. */}
      {riesgo.medidas.length === 0 && <Tag tone="amber">{t('perfilCargo.tabla.sinMedida')}</Tag>}
    </div>
  )
  const columnaAcciones = editable
    ? [columnaDeAcciones<Riesgo>({acciones, contexto: (riesgo) => corto(riesgo.peligro.texto)})]
    : []

  // En un teléfono cinco columnas no caben: el riesgo entero va en una celda, como en
  // `components/cumplimiento-fila.tsx`.
  const columnas: Column<Riesgo>[] = enTelefono
    ? [
        {
          key: 'riesgo',
          header: t('perfilCargo.tabla.peligro'),
          cell: (riesgo) => (
            <div className="min-w-0 space-y-1.5">
              <div className="font-medium">{clase(riesgo.peligro)}</div>
              {descripcion(riesgo)}
              {celdaMedidas(riesgo)}
              {estado(riesgo)}
            </div>
          ),
        },
        ...columnaAcciones,
      ]
    : [
        {
          key: 'peligro',
          header: t('perfilCargo.tabla.peligro'),
          width: 'w-36',
          cell: (riesgo) => clase(riesgo.peligro),
        },
        {key: 'descripcion', header: t('perfilCargo.tabla.descripcionPeligro'), width: 'w-1/3', cell: descripcion},
        {key: 'medidas', header: t('perfilCargo.tabla.medidas'), cell: celdaMedidas},
        {key: 'estado', header: t('perfilCargo.tabla.estado'), width: 'w-28', cell: estado},
        ...columnaAcciones,
      ]

  const columnasPropuestas: Column<PerfilLineaRead>[] = [
    {key: 'peligro', header: t('perfilCargo.tabla.peligro'), width: 'w-36', cell: clase},
    {
      key: 'descripcion',
      header: t('perfilCargo.tabla.descripcionPeligro'),
      width: 'w-2/5',
      cell: (peligro) => (
        <div className="space-y-1">
          <span className="whitespace-pre-wrap">{peligro.texto}</span>
          <OrigenLinea linea={peligro} businessId={businessId} desplegado={desplegado} />
        </div>
      ),
    },
    {
      key: 'medidas',
      header: t('perfilCargo.tabla.medidas'),
      cell: (peligro) => (
        <ul className="space-y-1">
          {medidasPropuestas
            .filter((medida) => medida.padre_clave === peligro.grupo_clave)
            .map((medida) => (
              <li key={medida.clave}>{medida.texto}</li>
            ))}
        </ul>
      ),
    },
    {
      key: 'acto',
      header: '',
      width: 'w-40',
      stopClick: true,
      cell: (peligro) =>
        editable ? (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" disabled={pending} onClick={() => onAdoptar(peligro)}>
              {t('perfilCargo.detalle.adoptar')}
            </Button>
          </div>
        ) : null,
    },
  ]

  return (
    <section className="surface rounded-lg p-4" data-testid="bloque-peligros">
      <CabeceraDeBloque
        bloque="peligros"
        editable={editable}
        pending={pending}
        onAgregar={() => onAbrirRiesgo({modo: 'nuevo'})}
      />
      {riesgos.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="bloque-vacio-peligros">
          {t('perfilCargo.detalle.sinLineas')}
        </p>
      ) : (
        <div className="mt-2">
          <DataTable
            columns={columnas}
            rows={riesgos}
            rowKey={(riesgo) => riesgo.peligro.clave}
            rowTestId={(riesgo) => `linea-${riesgo.peligro.clave}`}
            onRowClick={editable ? (riesgo) => onAbrirRiesgo({modo: 'editar', riesgo}) : undefined}
          />
        </div>
      )}

      {sueltas.length > 0 && (
        <div className="notice-amber mt-3 space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="medidas-sin-riesgo">
          <p>{t('perfilCargo.riesgo.sueltasAviso', {count: sueltas.length})}</p>
          <ul className="list-disc space-y-1 pl-4">
            {sueltas.map((medida) => (
              <li key={medida.clave} data-testid={`linea-${medida.clave}`}>
                <span className="whitespace-pre-wrap">{medida.texto}</span>
              </li>
            ))}
          </ul>
          {editable && (
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              data-testid="quitar-medidas-sin-riesgo"
              onClick={() => setQuitandoSueltas(true)}>
              {t('perfilCargo.riesgo.quitarSueltas', {count: sueltas.length})}
            </Button>
          )}
        </div>
      )}
      <ConfirmDialog
        open={quitandoSueltas}
        onOpenChange={setQuitandoSueltas}
        title={t('perfilCargo.riesgo.quitarSueltasTitulo', {count: sueltas.length})}
        description={t('perfilCargo.riesgo.quitarSueltasDetalle', {count: sueltas.length})}
        confirmLabel={t('perfilCargo.riesgo.quitar')}
        cancelLabel={t('common.cancel')}
        pending={pending}
        onConfirm={() => {
          onQuitarSueltas(sueltas)
          setQuitandoSueltas(false)
        }}
      />

      <Propuestas
        bloque={'peligros'}
        propuestas={peligros.propuestas}
        editable={editable}
        pending={pending}
        onAdoptarTodas={onAdoptarTodas}>
        <div className="mt-1">
          <DataTable
            columns={columnasPropuestas}
            rows={peligros.propuestas}
            rowKey={(peligro) => peligro.clave}
            rowTestId={(peligro) => `propuesta-${peligro.clave}`}
          />
        </div>
      </Propuestas>

      <div className="mt-4" data-testid="bloque-medidas_prevencion">
        <h4 className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('perfilCargo.tabla.medidasPropuestas')}
        </h4>
        {medidasPropuestas.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground" data-testid="sin-propuestas-medidas_prevencion">
            {t('perfilCargo.detalle.sinPropuestas')}
          </p>
        ) : (
          <>
            {editable && medidasAdoptables.length > 1 && (
              <Button
                className="mt-2"
                size="sm"
                variant="outline"
                disabled={pending}
                data-testid="adoptar-todas-medidas_prevencion"
                onClick={() => onAdoptarTodas(medidasAdoptables)}>
                {t('perfilCargo.detalle.adoptarTodas', {count: medidasAdoptables.length})}
              </Button>
            )}
            <ul className="mt-1 space-y-3">
              {medidasPropuestas.map((medida) => (
                <li key={medida.clave} className="space-y-1" data-testid={`propuesta-${medida.clave}`}>
                  <p className="text-sm text-foreground">{medida.texto}</p>
                  <OrigenLinea linea={medida} businessId={businessId} desplegado={desplegado} />
                  {editable && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pending || !adoptable(medida)}
                        onClick={() => onAdoptar(medida)}>
                        {t('perfilCargo.detalle.adoptar')}
                      </Button>
                      {!adoptable(medida) && (
                        <Hint testId={`propuesta-sin-peligro-${medida.clave}`}>
                          {t('perfilCargo.riesgo.agregueSuPeligro')}
                        </Hint>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  )
}
